import { resetTokenRecovery } from './tokenRecovery';
import keycloak, {
  setKeycloakInstance,
  resetKeycloakInstance,
} from './keycloak';
import type { KeycloakLike } from './keycloak';

export const LAST_AUTHENTICATED_USER_STORAGE_KEY = 'lastAuthenticatedUser';

const safeGetFromLocalStorage = (key: string): string | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const getLastAuthenticatedUserId = (): string | null => {
  return safeGetFromLocalStorage(LAST_AUTHENTICATED_USER_STORAGE_KEY);
};

let signedOut = false;
export const getCurrentUserId = (): string | null => {
  if (signedOut) return null;
  const keycloakUserId = keycloak.tokenParsed?.sub;
  if (keycloakUserId) {
    return keycloakUserId;
  }
  return getLastAuthenticatedUserId();
};

const listeners = new Set<() => void>();
let observedOwner: string | null = null;
let detachSession: (() => void) | undefined;

const publishIdentity = () => {
  const owner = getCurrentUserId();
  if (owner === observedOwner) return;
  observedOwner = owner;
  for (const listener of listeners) listener();
};
const onStorage = (event: StorageEvent) => {
  if (event.key === LAST_AUTHENTICATED_USER_STORAGE_KEY || event.key === null)
    publishIdentity();
};
export const subscribeUserIdentity = (listener: () => void) => {
  if (listeners.size === 0) {
    observedOwner = getCurrentUserId();
    window.addEventListener('storage', onStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
};

const rememberIdentity = () => {
  const owner = keycloak.tokenParsed?.sub;
  if (!signedOut && owner)
    localStorage.setItem(LAST_AUTHENTICATED_USER_STORAGE_KEY, owner);
  publishIdentity();
};

// Attach once to the SDK lifecycle; ordinary token refreshes emit no identity change.
export const startUserSession = (instance: KeycloakLike) => {
  resetTokenRecovery();
  detachSession?.();
  signedOut = false;
  setKeycloakInstance(instance);
  const cleanups = (
    ['onAuthSuccess', 'onAuthRefreshSuccess', 'onAuthLogout'] as const
  ).map((event) => {
    const previous = instance[event];
    const handler = () => {
      try {
        previous?.call(instance);
      } finally {
        rememberIdentity();
      }
    };
    instance[event] = handler;
    return () => {
      if (instance[event] === handler) instance[event] = previous;
    };
  });
  detachSession = () => {
    for (const cleanup of cleanups) cleanup();
  };
  rememberIdentity();
};

export const enterOfflineSession = () => {
  resetTokenRecovery();
  detachSession?.();
  detachSession = undefined;
  signedOut = false;
  resetKeycloakInstance();
  publishIdentity();
};

export const endUserSession = () => {
  resetTokenRecovery();
  // Suppress late refresh completions while logout navigation is pending.
  signedOut = true;
  localStorage.removeItem(LAST_AUTHENTICATED_USER_STORAGE_KEY);
  publishIdentity();
};
